import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { DialogService } from '@progress/kendo-angular-dialog';
import { round } from 'mathjs';
import { Observable, Subject } from 'rxjs';
import { SingleContactDto, BudgetSheetsService } from '@api-client';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import {
  AccountsService,
  CurrenciesService,
  ExpensesService,
  ExpenseCommandItem,
  CreateExpenseCommand,
  CurrencyRateDirectionType,
  CurrencyRatesService,
  Account,
  Currency,
  CurrencyRate
} from '@api-accounting-client';

import { JournalEditableGridModel } from '../journals/journalEditableGridModel';
import { AccountName } from '../../purchasing/purchase-invoice/purchaseInvoiceBaseComponent';
import { ExpensesEditableGridComponent } from './shared/components';
import { FormBaseComponent } from '../../shared/bases';
import {
  DialogExtendedService,
  NotificationsExtendedService
} from '../../shared/services';
import { SalesInvoiceDataSharingService } from '../../shared/services/data-helper';
import { SideBarDataSharingService } from '../../shared/services/side-bar';
import { ExpensesEditableGridModel } from './expensesEditableGridModel';
import { EditingContactData } from '../../shared/components/editing-contact/editingContactDataModel';
import { CurrencyInfo } from '../../shared/models';
import { endOfTenantDay, tenantNow } from '@app/shared/helpers/tenant-time';

export type ExpensesEditableGridModelExtended = ExpensesEditableGridModel &
  AccountName;

export type JournalEditableGridModelExtended = JournalEditableGridModel &
  AccountName;

@Component({
  template: '',
  standalone: false
})
export abstract class ExpenseFormBaseComponent
  extends FormBaseComponent<any>
  implements OnInit, OnDestroy
{
  @ViewChild('expensesEditableGrid')
  expensesEditableGrid: ExpensesEditableGridComponent;
  journalEntryData: JournalEditableGridModel[] = [];
  reference: any;
  totalBaseAmount = 0;
  isNotBalanced = false;
  accounts: Account[] = [];
  formArray: UntypedFormArray;
  gridData: ExpensesEditableGridModel[] = [];
  //minDate: Date = new Date();
  sendCustomer = new Subject<SingleContactDto>();
  contactFilter = {
    filters: [
      {
        field: 'isActive',
        operator: 'eq',
        value: true
      }
    ],
    logic: 'and'
  };
  accountFilter: CompositeFilterDescriptor = {
    filters: [
      {
        field: 'accountSubType.rootType',
        operator: 'eq',
        value: 1
      },
      {
        field: 'accountSubType.rootType',
        operator: 'eq',
        value: 2
      }
    ],
    logic: 'or'
  };
  currencyRateHint: string;
  currencies: Currency[] = [];
  totalAmount = 0;
  journalEntryDiscountItem: JournalEditableGridModelExtended = {};
  currencyRatesData: CurrencyRate[] = [];
  @Input() expenseUid: string;
  @Input() index = 0;
  @Input() isInDrawerMode: boolean;
  @Input() invoiceUid: string;

  @Output() closeDrawer = new EventEmitter();

  mode = '';
  actionMenuOptions = [{ label: 'editCurrency', value: 'Edit Currency' }];
  budgetItems: { uid: string; itemName: string }[] = [];

  onLoadData() {
    return {};
  }

  async loadBudgetItems(projectUid: string): Promise<void> {
    try {
      const result = await this.budgetSheetsService
        .budgetSheetsByProjectProjectUidGet(projectUid)
        .toPromise();
      console.log('[BUDGET_ITEMS] API response:', result);
      if (result?.phases) {
        // Flatten phases and their children into a flat list of items
        const next: { uid: string; itemName: string }[] = [];
        for (const phase of result.phases) {
          if (phase.children?.length > 0) {
            for (const child of phase.children) {
              next.push({ uid: child.uid, itemName: `${phase.itemName} > ${child.itemName}` });
            }
          } else {
            next.push({ uid: phase.uid, itemName: phase.itemName });
          }
        }
        this.budgetItems = next;
      } else {
        this.budgetItems = [];
      }
      console.log('[BUDGET_ITEMS] flattened budgetItems:', this.budgetItems);
    } catch (e) {
      console.log('[BUDGET_ITEMS] error:', e);
      this.budgetItems = [];
    }
  }

  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected expensesService: ExpensesService,
    protected dialogService: DialogService,
    protected accountsService: AccountsService,
    protected currenciesService: CurrenciesService,
    protected dialogExtendedService: DialogExtendedService,
    protected notificationsExtendedService: NotificationsExtendedService,
    protected sideBarDataSharingService: SideBarDataSharingService,
    protected salesInvoiceDataSharingService: SalesInvoiceDataSharingService,
    protected currencyRatesService: CurrencyRatesService,
    protected budgetSheetsService: BudgetSheetsService
  ) {
    super();
  }

  async ngOnInit() {
    super.ngOnInit();

    this.form = this.formBuilder.group({
      date: [tenantNow(), [Validators.required]],
      reference: [null],
      paymentAccountUid: [null, [Validators.required]],
      vendor: [null, [Validators.required]],
      projectUid: [null],
      currencyUid: [
        this.appSettingsService?.currenciesSettings?.defaultExpenseCurrency,
        [Validators.required]
      ],
      currencyRate: [1, [Validators.required]],
      saveCurrencyRate: [false]
    });

    // Load budget items when project changes
    const subProject = this.form.controls.projectUid.valueChanges.subscribe(
      async (projectUid: string) => {
        if (projectUid) {
          await this.loadBudgetItems(projectUid);
        } else {
          this.budgetItems = [];
        }
      }
    );
    this.subscriptions.push(subProject);

    const sub1 = this.sideBarDataSharingService.publishDataOnClose$.subscribe(
      async (data) => {
        if (data) {
          this.loadAccounts();
        }
      }
    );
    this.subscriptions.push(sub1);

    // On edit currency info in side drawer
    const sub2 = this.sideBarDataSharingService.publishDataOnClose$.subscribe(
      async (currency: CurrencyInfo) => {
        if (currency) {
          this.form.patchValue({
            currencyUid: currency.currencyUid,
            currencyRate: currency.currencyRate,
            saveCurrencyRate: currency.saveCurrencyRate
          });
        }
      }
    );
    this.subscriptions.push(sub2);

    // FIX ME: For Journal Entries
    // this.loadAccounts();

    // this.accounts = accountResult?.items.map((el) => {
    //   return {
    //     uid: el.uid,
    //     name: el.name,
    //     //hierarchyLevel: el.accountType?.hierarchyLevel,
    //     // naturalBalanceType: el.naturalBalanceType,
    //     fullName: (el.uid + ' - ' + el.name).trim(),
    //     currencyUid: el.currency?.uid
    //   };
    // }).map((el: MappedAccount) => {
    //   if (el.hierarchyLevel) {
    //     const whiteSpace = ' ';
    //     el.uid = whiteSpace.repeat(el.hierarchyLevel) + el.uid;
    //   }
    //   return el;
    // });

    this.form.valueChanges.subscribe(() => {
      if (!this.form.dirty) {
        this.isDirty = false;
      } else {
        this.isDirty = true;
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  async loadAccounts() {
    const accountResult = await this.accountsService
      .accountsGet(
        this.utilsService.generateDataSourceRequestString(
          null,
          null,
          null,
          [{ field: 'code', dir: 'asc' }],
          1,
          500
        )
      )
      .toPromise();
    this.accounts = accountResult?.items;
  }

  onVendorReceived(editingContactData: EditingContactData): void {
    if (editingContactData === null) {
      this.form.controls.vendor.setValue(null);
      return;
    }
  }

  sendCustomerToEditContact(value: SingleContactDto | null) {
    this.sendCustomer.next(value);
    //FIXME commented out based on requirement change
    // this.createJournalEntryItems();
  }

  async submitRecord(_mode?: string, _actionName?: string) {
    super.submit();

    // ZEY-387: a foreign-currency expense must carry a real conversion rate. The Edit-Currency
    // drawer's async rate fetch can race with save and leave the form rate at its default 1,
    // which the backend stores unconverted (75 EUR posted as 75 QAR). Resolve/verify it here,
    // race-free, BEFORE the validity check so a fetchable rate is applied (clearing the rate==1
    // error) and an unresolvable one blocks the save.
    const currencyRate = await this.resolveForeignCurrencyRate();
    if (currencyRate === null) {
      this.dialogExtendedService.showValidationDialog(
        'currencyRateCannotBeOneForForeignCurrency'
      );
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.formArray = this.expensesEditableGrid.getData();

    for (const form of (<UntypedFormGroup[]>this.formArray.controls).slice(
      0,
      -1
    )) {
      if (form.invalid) {
        this.dialogExtendedService.showValidationDialog('yourFormIsNotValid');
        return;
      }
    }

    const ExpenseItems: ExpenseCommandItem[] = [];
    
    let item: ExpenseCommandItem;
    for (const form of (<UntypedFormGroup[]>this.formArray.controls).slice(
      0,
      -1
    )) {
      item = {
        expenseAccountUid: form.value.accountUid.trim(),
        amount: form.value.amount,
        notes: form.value.remark,
        taxUids: form.value.tax ? [form.value.tax] : []
      };
      ExpenseItems.push(item);
    }

    if (ExpenseItems.length === 0) {
      this.dialogExtendedService.showValidationDialog(
        'youHaveNoExpensesItemsAdded'
      );
      return;
    }

    this.isWaiting = true;
    const command: CreateExpenseCommand = {
      reference: this.form.value.reference,
      date: this.form.value.date,
      expenseItems: ExpenseItems,
      paymentAccountUid: this.form.value.paymentAccountUid.trim(),
      vendorUid: this.form.value.vendor.uid,
      projectUid: this.form.value.projectUid,
      currencyUid: this.form.value.currencyUid,
      currencyRate: currencyRate,
      saveCurrencyRate: this.form.value.saveCurrencyRate
    };

    if (this.invoiceUid) {
      command.purchaseInvoiceUid = this.invoiceUid;
    }

    const response = await this.onDataSubmit(command).toPromise();
    this.isWaiting = false;
    this.isDirty = false;
    this.expenseUid = response ? response.uid : command.reference;

    if (_actionName == 'Save and Close') {
      this.isInDrawerMode
        ? this.closeSideDrawer()
        : this.appSettingsService.navigateBack();
    }

    if (_actionName == 'Save and New') {
      if (_mode === 'add') {
        this.notificationsExtendedService.showNotification(
          this.translate.instant('expenseHasBeenCreated')
        );
        this.reloadRoute();
      } else if (_mode === 'edit') {
        this.goToAddPage();
      }
    }
  }

  async closeSideDrawer(): Promise<void> {
    if (this.expenseUid) {
      this.closeDrawer.emit(this.expenseUid);
    } else {
      this.closeDrawer.emit();
    }
  }

  dirtyFormHandler(isDirty: boolean) {
    if (isDirty) {
      this.isDirty = true;
    }
  }

  goToListPage() {
    this.router.navigate(['/expenses']);
  }

  goToAddPage() {
    this.router.navigate(['/expenses/add']);
  }

  goToViewPage(event: any): void {
    this.router.navigate(['/expenses/view'], {
      queryParams: { id: event }
    });
  }

  openCurrencyInfoAddDrawer() {
    const { currencyUid, currencyRate } = this.form.value;
    this.sideBarDataSharingService.sendCurrencyInfoData({
      currencyUid: currencyUid,
      currencyRate: currencyRate,
      mode: this.mode,
      date: this.form.value.date
    });
  }

  onActionClick(value: any, _mode?: string) {
    switch (value) {
      case 'Save and Close':
        this.submitRecord(_mode, value);
        break;
      case 'Save and New':
        this.submitRecord(_mode, value);
        break;
      case 'Cancel':
        this.appSettingsService.navigateBack();
        break;
      case 'Discard':
        this.isInDrawerMode ? this.closeSideDrawer() : this.goToListPage();
        break;
    }
  }

  @HostListener('document:keydown.escape') onEscapeHandler() {
    if (!this.dialogExtendedService.closeValidationDialog()) {
      if (!this.isDrawerOpened) {
        if (this.isInDrawerMode) {
          this.closeSideDrawer();
        } else {
          this.goToListPage();
        }
      }
    }
  }

  async createJournalEntryItems() {
    if (!this.form.controls.paymentAccountUid.valid) {
      return;
    }
    const paymentAccount = this.accounts.find(
      (paymentAccount) =>
        paymentAccount.uid.trim() === this.form.value.paymentAccountUid.trim()
    );

    if (!paymentAccount) {
      return;
    }

    const paymentAccountCurrencyUid = paymentAccount.currency?.uid; //.currencyUid;
    this.currencyRatesData = this.currencyRatesData.filter(
      (currency) => currency.uid === paymentAccountCurrencyUid
    );
    // this.currencyRatesData = await this.currencyRatesService
    // .currencyRatesSymbolRatesGet(
    //   paymentAccountCurrencyUid
    //   this.utilsService.generateDataSourceRequestString(
    //     null,
    //     null,
    //     null,
    //     [{ field: 'date', dir: 'desc' }]
    //   )
    // )
    //   .pipe(map((x) => x.items))
    //   .toPromise();

    // const paymentAccountCurrency = this.currencies.find(
    //   (currency) => currency.uid === paymentAccountCurrencyUid
    // );

    //For Mono Currency
    const paymentAccountCurrency = paymentAccount.currency;

    const paymentAccountCurrencyRate =
      this.currencyRatesData.length > 0
        ? paymentAccountCurrency.defaultRateDirectionType ===
          CurrencyRateDirectionType.FromBase
          ? this.currencyRatesData[0].rateFromBase
          : this.currencyRatesData[0].rateToBase
        : 1;

    let totalPaidAmount = 0;
    const data: JournalEditableGridModelExtended[] = [];

    for (const formGroup of (<UntypedFormGroup[]>(
      this.expensesEditableGrid?.getData().controls
    ))?.slice(0, -1)) {
      if (formGroup.value.baseAmount) {
        // const currency = this.currencies.find(
        //   (currency) => currency.uid === formGroup.value.currency
        // );
        const expenseAccount = this.accounts.find(
          (expenseAccount) =>
            expenseAccount.uid.trim() === formGroup.value.accountUid.trim()
        );
        const journalEntryNetItem: JournalEditableGridModelExtended = {
          accountName: expenseAccount?.uid,
          isReadOnly: true,
          remark: formGroup.value.remark,
          currency: formGroup.value.currency,
          rate: formGroup.value.rate,
          credit: 0,
          debit: round(formGroup.value.amount, 2),
          baseAmount: round(-formGroup.value.baseAmount, 2),
          accountUid: expenseAccount?.uid + ' - ' + expenseAccount?.name
        };
        data.push(journalEntryNetItem);
        totalPaidAmount += formGroup.value.baseAmount;
      }
    }

    const journalEntryCustomerItem: JournalEditableGridModelExtended = {
      accountName: paymentAccount?.uid,
      accountUid: paymentAccount?.uid + ' - ' + paymentAccount?.name,
      currency: paymentAccountCurrency.uid,
      credit: round(totalPaidAmount, 2),
      debit: 0,
      rate: paymentAccountCurrencyRate,
      remark: null,
      isReadOnly: true,
      baseAmount: round(totalPaidAmount, 2)
    };

    paymentAccountCurrency.defaultRateDirectionType ===
    CurrencyRateDirectionType.FromBase
      ? (journalEntryCustomerItem.credit /= paymentAccountCurrencyRate)
      : (journalEntryCustomerItem.credit *= paymentAccountCurrencyRate);

    data.push(journalEntryCustomerItem);

    this.totalAmount = 0;

    for (const record of data) {
      this.totalAmount += round(record.baseAmount, 2);
    }

    this.salesInvoiceDataSharingService.sendData(data);
    this.salesInvoiceDataSharingService.sendTotalBaseAmount(
      round(this.totalAmount, 2)
    );
  }

  /**
   * ZEY-387: returns the effective transaction-currency rate to submit, or null to block save.
   * - Base currency: rate is 1.
   * - Foreign currency with an explicit rate (!= 1): trust it.
   * - Foreign currency still at the default rate 1: resolve the tenant's maintained rate.
   *   Returns null when no valid (non-1) maintained rate exists — saving unconverted is blocked.
   */
  private async resolveForeignCurrencyRate(): Promise<number | null> {
    const baseCurrency =
      this.appSettingsService?.currenciesSettings?.baseCurrency;
    const currencyUid = this.form.value.currencyUid;
    const currentRate = this.form.value.currencyRate;

    if (!currencyUid || currencyUid === baseCurrency) {
      return currentRate || 1;
    }
    if (currentRate && currentRate !== 1) {
      return currentRate;
    }

    const maintained = await this.fetchMaintainedRate(currencyUid);
    if (!maintained || maintained === 1) {
      return null;
    }
    this.form.patchValue({ currencyRate: maintained });
    return maintained;
  }

  private async fetchMaintainedRate(
    currencyUid: string
  ): Promise<number | null> {
    try {
      const [currency, latest] = await Promise.all([
        this.currenciesService.currenciesUidGet(currencyUid).toPromise(),
        this.currencyRatesService
          .currencyRatesSymbolRatesLatestGet(
            currencyUid,
            endOfTenantDay(this.form.value.date)
          )
          .toPromise()
      ]);
      if (!latest) {
        return null;
      }
      return currency?.defaultRateDirectionType ===
        CurrencyRateDirectionType.FromBase
        ? latest.rateFromBase
        : latest.rateToBase;
    } catch {
      return null;
    }
  }

  abstract onDataSubmit<TCommand extends unknown>(
    command: TCommand
  ): Observable<any>;
}
